<template>
  <div class="admin-jobs-page">
    <div class="page-header">
      <h1 class="page-title">공고 등급 관리</h1>
    </div>

    <!-- 필터 -->
    <div class="filter-bar">
      <select v-model="filterGrade">
        <option value="">전체 등급</option>
        <option value="premium">프리미엄</option>
        <option value="pick">분쟁PICK</option>
        <option value="featured">추천</option>
        <option value="urgent">급구</option>
        <option value="normal">일반</option>
      </select>
      <input type="text" v-model="searchQuery" placeholder="공고명 검색..." />
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else class="job-list">
      <div v-for="job in filteredJobs" :key="job.id" class="job-item">
        <div class="job-thumbnail">
          <img :src="job.thumbnail || 'https://picsum.photos/100/60?random=' + job.id" :alt="job.siteName" />
        </div>
        <div class="job-info">
          <h3>{{ job.title }}</h3>
          <p>{{ job.siteName }} · {{ job.region }}</p>
        </div>
        <div class="job-grade">
          <select v-model="job.grade" @change="updateGrade(job)">
            <option value="normal">일반</option>
            <option value="urgent">급구</option>
            <option value="featured">추천</option>
            <option value="pick">분쟁PICK</option>
            <option value="premium">프리미엄</option>
          </select>
        </div>
        <div class="job-badges">
          <label class="badge-checkbox">
            <input type="checkbox" v-model="job.isPremium" @change="updateJob(job)" />
            <span class="badge premium">프리미엄</span>
          </label>
          <label class="badge-checkbox">
            <input type="checkbox" v-model="job.isPick" @change="updateJob(job)" />
            <span class="badge pick">분쟁PICK</span>
          </label>
          <label class="badge-checkbox">
            <input type="checkbox" v-model="job.isUrgent" @change="updateJob(job)" />
            <span class="badge urgent">급구</span>
          </label>
          <label class="badge-checkbox">
            <input type="checkbox" v-model="job.isFeatured" @change="updateJob(job)" />
            <span class="badge featured">추천</span>
          </label>
        </div>
        <router-link :to="`/jobs/${job.id}`" class="btn-view">보기</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, getDocs, updateDoc, doc, orderBy, query, serverTimestamp } from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'AdminJobsView',
  setup() {
    const jobs = ref([])
    const loading = ref(true)
    const filterGrade = ref('')
    const searchQuery = ref('')

    const fetchJobs = async () => {
      loading.value = true
      try {
        const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        jobs.value = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          grade: doc.data().grade || 'normal',
          isPremium: doc.data().isPremium || false,
          isPick: doc.data().isPick || false,
          isUrgent: doc.data().isUrgent || false,
          isFeatured: doc.data().isFeatured || false
        }))
      } catch (e) {
        console.error('공고 로드 실패:', e)
      } finally {
        loading.value = false
      }
    }

    const filteredJobs = computed(() => {
      let result = jobs.value

      if (filterGrade.value) {
        if (filterGrade.value === 'pick') {
          result = result.filter(job => job.isPick === true)
        } else {
          result = result.filter(job => job.grade === filterGrade.value)
        }
      }

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(job => 
          job.title?.toLowerCase().includes(query) ||
          job.siteName?.toLowerCase().includes(query)
        )
      }

      return result
    })

    const updateGrade = async (job) => {
      try {
        await updateDoc(doc(db, 'jobs', job.id), {
          grade: job.grade,
          isPremium: job.grade === 'premium',
          updatedAt: serverTimestamp()
        })
        job.isPremium = job.grade === 'premium'
      } catch (e) {
        console.error('등급 변경 실패:', e)
      }
    }

    const updateJob = async (job) => {
      try {
        await updateDoc(doc(db, 'jobs', job.id), {
          isPremium: job.isPremium,
          isPick: job.isPick,
          isUrgent: job.isUrgent,
          isFeatured: job.isFeatured,
          updatedAt: serverTimestamp()
        })
      } catch (e) {
        console.error('공고 업데이트 실패:', e)
      }
    }

    onMounted(fetchJobs)

    return {
      jobs,
      loading,
      filterGrade,
      searchQuery,
      filteredJobs,
      updateGrade,
      updateJob
    }
  }
}
</script>

<style scoped>
.admin-jobs-page {
  padding: 40px 24px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
}

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.filter-bar select,
.filter-bar input {
  padding: 12px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  background: #fff;
}

.filter-bar input {
  flex: 1;
}

.loading-state {
  text-align: center;
  padding: 60px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.job-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.job-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.job-thumbnail {
  width: 80px;
  height: 50px;
  border-radius: 8px;
  overflow: hidden;
  background: #f2f4f6;
  flex-shrink: 0;
}

.job-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.job-info {
  flex: 1;
  min-width: 0;
}

.job-info h3 {
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.job-info p {
  font-size: 13px;
  color: #6b7684;
}

.job-grade select {
  padding: 8px 12px;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}

.job-badges {
  display: flex;
  gap: 8px;
}

.badge-checkbox {
  cursor: pointer;
}

.badge-checkbox input {
  display: none;
}

.badge {
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  opacity: 0.4;
  transition: opacity 0.15s;
}

.badge-checkbox input:checked + .badge {
  opacity: 1;
}

.badge.premium {
  background: #3182f6;
  color: #fff;
}

.badge.pick {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #fff;
}

.badge.urgent {
  background: #e03131;
  color: #fff;
}

.badge.featured {
  background: #fab005;
  color: #191f28;
}

.btn-view {
  padding: 8px 16px;
  background: #f2f4f6;
  color: #4e5968;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
}

.btn-view:hover {
  background: #e5e8eb;
}

@media (max-width: 768px) {
  .filter-bar {
    flex-direction: column;
  }

  .job-item {
    flex-wrap: wrap;
  }

  .job-badges {
    width: 100%;
    justify-content: flex-start;
    margin-top: 8px;
  }
}
</style>